import React, {useEffect, useState} from "react";
import {WaterDTO} from "../../dtos/WaterDTO";
import { NetworkCalls } from "../../NetworkCalls";
import EmptyPage from "../../components/EmptyPage";
import {Button} from "react-bootstrap";
import "suneditor/dist/css/suneditor.min.css";
import WaterContactPersonVerwaltung from "./WaterContactPersonVerwaltung";
import {Link} from "react-router-dom";
import WaterNameAndDescriptionView from "./WaterNameAndDescriptionView";
import {FormProvider, SubmitHandler, useForm} from "react-hook-form";
import WaterFishOccurenceView from "./WaterFishOccurenceView";
import GewaesserumrandungVerwaltung from "./GewaesserumrandungVerwaltung";
import AddNewWaterDialog from "../../components/AddNewWaterDialog";
import WaterFishProtectionView from "./WaterFishProtectionView";
import {Fish} from "../../dtos/Fish";
import DeleteDialog from "../../components/DeleteDialog";
import WaterUndergroundCarousel from "./WaterUndergroundMapList";
import {useSelectedOrganisationContext} from "../../custom-hooks/useOrganisation";
import "./WaterListOverview.css";

export default function WaterListOverview() {

    const formMethods = useForm<WaterDTO>();
    const {
        control,
        register,
        unregister,
        formState: {errors},
        setValue,
        reset,
        handleSubmit,
    } = formMethods;

    const [waterList, setWaterList] = useState<WaterDTO[]>();
    const [selectedWater, setSelectedWater] = useState<WaterDTO | undefined>();
    const [tabIndex, setTabIndex] = useState<number>(0);
    const [showAddWaterDialog, setShowAddWaterDialog] = useState<boolean>(false);
    const [showDeleteWaterDialog, setShowDeleteWaterDialog] = useState<boolean>(false);
    const [isLoadingWaters, setIsLoadingWaters] = useState<boolean>(true);

    const { selectedOrganisation } = useSelectedOrganisationContext();

    const [availableFishes, setAvailableFishes] = useState<Fish[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setAvailableFishes(await NetworkCalls.getOrganisationFishes());
        };
        fetchData().then();
    }, []);

    const onSubmit: SubmitHandler<WaterDTO> = async (data) => {
        if (!selectedOrganisation) {
            return;
        }

        data.organisationId = selectedOrganisation.id;
        data.id = selectedWater?.id!;

        setSelectedWater(await NetworkCalls.persistWater(data));
        if (waterList) {
            let newArr = [...waterList];
            newArr[newArr.findIndex((water) => water.id === data.id)] = data;
            setWaterList(newArr);

        }

        setTabIndex(0);
    };

    const inactiveClasses = "nav-link";
    const activeClasses = "nav-link active";

    useEffect(() => {
        const fetchData = async () => {
            setIsLoadingWaters(true);
            const waters = await NetworkCalls.listWater();
            setWaterList(waters);
            setIsLoadingWaters(false);
        };
        fetchData().then();
    }, []);

    useEffect(() => {
        if (selectedWater) {
            reset({
                ...selectedWater,
                name: selectedWater.name ?? "",
                description: selectedWater.description ?? ""
            });
        }
    }, [reset, selectedWater, selectedWater?.id]);

    const isActiveWater = (water?: WaterDTO) =>
        water?.id && selectedWater?.id && water.id === selectedWater.id;

    function getWaterDetailsHeadingLi(idx: number, label: string) {
        return <li className="nav-item">
            <Link
                to="#"
                className={tabIndex === idx ? activeClasses : inactiveClasses}
                onClick={() => setTabIndex(idx)}
            >
                {label}
            </Link>
        </li>;
    }

    const getWaterDetails1 = () => {
        return (
            <FormProvider key={selectedWater?.id ?? "water-form"} {...formMethods}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <div className={"w-100"}>
                        <div className="card">
                            <div className="card-header">
                                <ul className="nav nav-tabs card-header-tabs">
                                    {['Allgemein', 'Umrandungen', 'Kontaktpersonen', 'Fischvorkommen', 'Schonzeiten', 'Tiefenkarte']
                                        .map((v, idx) => (
                                            <React.Fragment key={v}>
                                                {getWaterDetailsHeadingLi(idx, v)}
                                            </React.Fragment>
                                        ))}
                                </ul>
                            </div>
                            <div className="card-body">
                                <WaterNameAndDescriptionView
                                    key={selectedWater?.id ?? "water-details"}
                                    register={register}
                                    setValue={setValue}
                                    control={control}
                                    visible={tabIndex === 0}
                                    water={selectedWater}
                                    errors={errors}
                                />
                                <GewaesserumrandungVerwaltung
                                    setValue={setValue}
                                    visible={tabIndex === 1}
                                    waterId={selectedWater?.id}
                                />
                                <WaterContactPersonVerwaltung
                                    contactPersons={selectedWater?.contactPersons}
                                    visible={tabIndex === 2}
                                    register={register}
                                    unregister={unregister}
                                    setValue={setValue}
                                    errors={errors}
                                />
                                <WaterFishOccurenceView
                                    fishes={selectedWater?.waterFishes}
                                    visible={tabIndex === 3}
                                    register={register}
                                    setValue={setValue}
                                />
                                <WaterFishProtectionView
                                    visible={tabIndex === 4}
                                    availableFishes={availableFishes}
                                    formPath={'waterFishProtections'}
                                />
                                <WaterUndergroundCarousel
                                    waterId={selectedWater?.id}
                                    visible={tabIndex === 5}
                                    register={register}
                                    setValue={setValue}
                                />
                            </div>
                            <div className="card-footer">
                                <div className="d-flex">
                                    <Button className="mt-3" type={"submit"}>
                                        Speichern
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </FormProvider>
        );
    };

    const getContent = () => {
        return (
            <div className="container-fluid px-0" style={{ height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
                <div className="row g-0 h-100">
                    <div
                        className="col-12 col-md-3 bg-light border-end h-100 d-flex flex-column"
                        style={{ overflow: 'hidden' }}
                    >
                        <div className="p-3">
                            <AddNewWaterDialog afterSuccess={async () => {
                                setShowAddWaterDialog(false);
                                const waters = await NetworkCalls.listWater();
                                setWaterList(waters);
                            }} onClose={() => {
                                setShowAddWaterDialog(false)
                            }} show={showAddWaterDialog}/>
                            <DeleteDialog onDeleteConfirm={async () => {
                                setShowDeleteWaterDialog(false);
                                if (!selectedWater) {
                                    return;
                                }
                                await NetworkCalls.deleteWater(selectedWater);
                                const waters = await NetworkCalls.listWater();
                                setWaterList(waters);
                                setSelectedWater(undefined);
                            }} onHandleClose={() => {
                                setShowDeleteWaterDialog(false);
                            }} title={"Gewässer löschen?"} message={`Möchtest du das Gewässer ${selectedWater?.name} wirklich löschen?`} show={showDeleteWaterDialog} />
                            <div className="card border-0 bg-transparent h-100">
                                <div className="card-header bg-transparent border-0 d-flex align-items-center justify-content-between">
                                    <h3 className="card-title m-0">Gewässerübersicht</h3>
                                    <Button
                                        onClick={() => setShowAddWaterDialog(true)}
                                    >
                                        Neues Gewässer
                                    </Button>
                                </div>
                                <div
                                    className="list-group list-group-flush"
                                    style={{
                                        overflowY: "auto",
                                        maxHeight: "calc(100vh - 220px)"
                                    }}
                                >
                                    {isLoadingWaters ? (
                                        <>
                                            {[...Array(8)].map((_, idx) => (
                                                <div key={idx} className="list-group-item border-0 bg-transparent py-3 water-list-item" style={{ cursor: 'default' }}>
                                                    <div className="d-flex w-100 justify-content-between align-items-center">
                                                        <div className="me-2 flex-grow-1">
                                                            <div
                                                                style={{
                                                                    height: '1.2em',
                                                                    backgroundColor: '#dee2e6',
                                                                    borderRadius: '4px',
                                                                    width: '60%',
                                                                    animation: 'pulse 1.5s ease-in-out infinite'
                                                                }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}</>
                                    ) : (
                                        (waterList ?? []).map((water) => (
                                            <div
                                                key={water.id}
                                                className={`list-group-item list-group-item-action border-0 bg-transparent py-3 water-list-item${isActiveWater(water)
                                                    ? ' active'
                                                    : ''
                                                    }`}
                                                style={{ position: 'relative', paddingLeft: '1.25rem' }}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => {
                                                    setSelectedWater(water);
                                                    setTabIndex(0);
                                                    reset({
                                                        ...water,
                                                        name: water.name ?? "",
                                                        description: water.description ?? ""
                                                    });
                                                }}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter" || event.key === " ") {
                                                        event.preventDefault();
                                                        setSelectedWater(water);
                                                        setTabIndex(0);
                                                    }
                                                }}
                                            >
                                                <div className="d-flex w-100 justify-content-between align-items-center">
                                                    <div className="me-2 text-truncate">
                                                        <div className="fw-semibold text-truncate text-body">
                                                            {water.name}
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className={
                                                            "btn btn-link p-0 text-decoration-none text-danger"
                                                        }
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setSelectedWater(water);
                                                            setShowDeleteWaterDialog(true);
                                                        }}
                                                        aria-label="Gewässer löschen"
                                                        title="Löschen"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="icon icon-tabler icon-tabler-trash"
                                                            width="20"
                                                            height="20"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth="2"
                                                            stroke="currentColor"
                                                            fill="none"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                            <path d="M4 7l16 0" />
                                                            <path d="M10 11l0 6" />
                                                            <path d="M14 11l0 6" />
                                                            <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                                                            <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                    {!isLoadingWaters && (waterList ?? []).length === 0 && (
                                        <div className="list-group-item text-muted py-4 text-center">
                                            Keine Gewässer gefunden
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className="col-12 col-md-9 h-100"
                        style={{
                            overflowY: "auto",
                            padding: "1.5rem"
                        }}
                    >
                        {selectedWater ? (
                            getWaterDetails1()
                        ) : (
                                <div className="d-flex align-items-center justify-content-center" style={{ height: "100%" }}>
                                    <EmptyPage
                                        addNewElement={false}
                                        text="Bitte wähle ein Gewässer aus"
                                    />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return getContent();
}
